import { imageFormats } from "../config";
import { ImageSizes } from "../types";
import { Logger } from "@fruugo/utilities";

const Log = new Logger(window.environment);

export const toggleClass = (element: Element, className: string) => {
  if (!element) return;
  if (element.classList.contains(className)) {
    element.classList.remove(className);
  } else {
    element.classList.add(className);
  }
};

export const getKeyboardFocusableElements = (
  element?: HTMLElement | Document
): HTMLElement[] => {
  const root: HTMLElement | Document = element || document;
  const selector =
    'a[href], button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])';
  const nodeList = root.querySelectorAll(selector);
  return Array.from(nodeList).filter(
    (el): el is HTMLElement =>
      el instanceof HTMLElement &&
      !el.hasAttribute("disabled") &&
      el.getAttribute("aria-hidden") !== "true"
  );
};

export const disableBodyScroll = () => {
  document.documentElement.style.scrollbarGutter = "stable";
  document.body.classList.add("tw:overflow-y-hidden");
};

export const enableBodyScroll = () => {
  document.body.classList.remove("tw:overflow-y-hidden");
  document.documentElement.style.scrollbarGutter = "";
};

export const slideUp = (target: HTMLElement, duration = 300) => {
  target.style.maxHeight = target.scrollHeight + "px";
  target.style.maxHeight = "0";
  target.style.display = "none";
  window.setTimeout(function () {
    target.classList.remove("is-visible");
  }, duration);
};

export const slideDown = (target: HTMLElement) => {
  if (!target) return;
  const getHeight = function () {
    target.style.display = "block";
    const height = target.scrollHeight + "px";
    target.style.display = "";
    return height;
  };
  const height = getHeight();
  target.classList.add("is-visible");
  target.style.maxHeight = height;
};

export const slideToggle = (target: HTMLElement, duration = 300) => {
  if (!target) return;
  if (!target.classList.contains("is-visible")) {
    return slideDown(target);
  } else {
    return slideUp(target, duration);
  }
};

export function stringToHtml(str: string) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(str, "text/html");
  const html = doc.body.children;
  if (html.length > 1) {
    Log.warning(`stringToHtml only returns single children. str: ${str}`);
  }
  return html[0];
}

export function minWidth(
  width: number,
  minWidthFn: () => unknown,
  maxWidthFn: () => unknown
) {
  let matchesCheck: boolean | null = null;
  function runCallbacks() {
    const mediaQuery = window.matchMedia(`(min-width: ${width}px)`);
    const { matches } = mediaQuery;
    // Only run this when matches changes rather than on every resize event
    if (matchesCheck === null || matchesCheck != matches) {
      if (matches) {
        minWidthFn && minWidthFn();
      } else {
        maxWidthFn && maxWidthFn();
      }
    }
    matchesCheck = matches;
  }
  runCallbacks();
  window.addEventListener("resize", runCallbacks, false);
}

const getViewportWidth = () =>
  window.innerWidth || document.documentElement.clientWidth || 0;

export const isDeviceWidthBetween = (min: number, max?: number) => {
  const viewportWidth = getViewportWidth();
  const meetsMin = viewportWidth >= min;
  const belowMax = typeof max === "number" ? viewportWidth < max : true;
  return meetsMin && belowMax;
};

export const isMobileDevice = (): boolean => {
  const mobileDevices = [
    "Android",
    "webOS",
    "iPhone",
    "iPad",
    "iPod",
    "BlackBerry",
    "IEMobile",
    "Opera Mini",
  ];
  return mobileDevices.some((device) => navigator.userAgent.includes(device));
};

export const createPictureElement = (
  url: string,
  sizes: ImageSizes[],
  alt: string,
  cssClass: string,
  ariaHidden: boolean = false
) => {
  return (
    <picture className={cssClass}>
      {imageFormats.map((format) => (
        <source
          type={`image/${format.toLowerCase()}`}
          srcSet={sizes
            .filter((size) => size.format === format)
            .reduce(
              (prev, current) => `${current.src} ${current.width}w,${prev}`,
              ""
            )}
        ></source>
      ))}
      <img
        src={url}
        alt={alt}
        className={cssClass}
        aria-hidden={ariaHidden ? "true" : "false"}
      />
    </picture>
  );
};

export const styleSecondWord = (element: Element) => {
  if (!element) return;

  const words = element.innerHTML.split(" ");

  if (words.length <= 1) return;
  words[1] = `<span class='second-word'>${words[1]}</span>`;
  element.innerHTML = words.join(" ");
};

export function getQueryParam(param: string) {
  const params = new URLSearchParams(window.location.search);

  const value = params.get(param);

  return value ? decodeURIComponent(value) : undefined;
}
